import { getConfig } from '../config'
import type {
  Event,
  Registration,
  Team,
  Stats,
  RegistrationFormData,
  CheckoutResponse,
  RegistrationModeResponse,
  WaitlistRegistrationResponse,
  SponsorshipFormData,
  SponsorshipSubmission,
  SponsorshipConfig,
  SponsorshipCheckoutResponse,
  SponsorshipStats,
  AmbassadorFormData,
  AmbassadorSubmission,
  AmbassadorCheckoutResponse,
  AmbassadorStats,
  Coupon,
  CouponUsage,
  ValidateCouponResponse,
} from '../types'

const config = getConfig()

// Custom error class for authentication errors
export class AuthError extends Error {
  constructor(message: string, public statusCode: number) {
    super(message)
    this.name = 'AuthError'
  }
}

// Event emitter for auth errors - components can subscribe to handle session expiry
type AuthErrorHandler = () => void
let authErrorHandler: AuthErrorHandler | null = null

export function setAuthErrorHandler(handler: AuthErrorHandler) {
  authErrorHandler = handler
}

export function clearAuthErrorHandler() {
  authErrorHandler = null
}

async function fetchApi<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${config.apiUrl}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  })

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error || 'An error occurred')
  }

  return data
}

async function fetchApiWithAuth<T>(
  endpoint: string,
  token: string,
  options: RequestInit = {}
): Promise<T> {
  const url = `${config.apiUrl}${endpoint}`

  const response = await fetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${token}`,
      ...options.headers,
    },
  })

  // Handle authentication errors (expired token, invalid token, etc.)
  if (response.status === 401 || response.status === 403) {
    // Trigger auth error handler to log out user and redirect to login
    if (authErrorHandler) {
      authErrorHandler()
    }
    throw new AuthError('Your session has expired. Please log in again.', response.status)
  }

  const data = await response.json()

  if (!response.ok) {
    throw new Error(data.error || 'An error occurred')
  }

  return data
}

// Public API
export const publicApi = {
  async getEvents(): Promise<{ events: Event[] }> {
    return fetchApi('/public/events')
  },

  async getEventBySlug(slug: string): Promise<Event> {
    return fetchApi(`/public/events/${slug}`)
  },

  async createCheckoutSession(
    data: RegistrationFormData
  ): Promise<CheckoutResponse> {
    return fetchApi('/public/create-checkout-session', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getRegistrationState(slug: string): Promise<RegistrationModeResponse> {
    return fetchApi(`/public/event/${slug}/registration-state`)
  },

  async joinWaitlist(
    data: RegistrationFormData
  ): Promise<WaitlistRegistrationResponse> {
    return fetchApi('/public/join-waitlist', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getWaitlistRegistration(
    token: string
  ): Promise<{
    registration: {
      registrationId: string
      eventId: string
      status: string
      primaryContact: { name: string; email: string; phone: string }
      golfersCount: number
      banquetTicketsCount: number
      golfers: Array<{ name: string; email: string; phone: string }>
      teamName?: string
      waitlistPosition: number
      createdAt: string
    }
    event: {
      eventId: string
      name: string
      slug: string
      date: string
      venue: string
    }
  }> {
    return fetchApi(`/public/waitlist-registration/${token}`)
  },

  async createWaitlistCheckout(
    data: RegistrationFormData & { waitlistToken: string }
  ): Promise<CheckoutResponse> {
    return fetchApi('/public/waitlist-checkout', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Sponsorship endpoints
  async getSponsorshipConfig(slug: string): Promise<SponsorshipConfig> {
    return fetchApi(`/public/events/${slug}/sponsorship`)
  },

  async submitSponsorship(
    data: SponsorshipFormData
  ): Promise<{ submissionId: string; status: string; amountDueCents: number }> {
    return fetchApi('/public/sponsorship/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async createSponsorshipCheckout(
    submissionId: string
  ): Promise<SponsorshipCheckoutResponse> {
    return fetchApi('/public/sponsorship/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ submissionId }),
    })
  },

  async getSponsorshipSubmission(
    submissionId: string
  ): Promise<{ submission: SponsorshipSubmission }> {
    return fetchApi(`/public/sponsorship/${submissionId}`)
  },

  async getSponsorLogoUploadUrl(
    fileName: string,
    contentType: string
  ): Promise<{ uploadUrl: string; logoKey: string }> {
    return fetchApi('/public/sponsorship/logo-upload-url', {
      method: 'POST',
      body: JSON.stringify({ fileName, contentType }),
    })
  },

  async getSponsorLogoInfo(
    submissionId: string
  ): Promise<{ sponsorName: string; hasLogo: boolean }> {
    return fetchApi(`/public/sponsorship/${submissionId}/logo-info`)
  },

  async updateSponsorLogo(
    submissionId: string,
    logoKey: string
  ): Promise<{ message: string }> {
    return fetchApi(`/public/sponsorship/${submissionId}/logo`, {
      method: 'PUT',
      body: JSON.stringify({ logoKey }),
    })
  },

  // Ambassador endpoints
  async submitAmbassador(
    data: AmbassadorFormData
  ): Promise<{ ambassadorId: string; status: string; amountDueCents: number }> {
    return fetchApi('/public/ambassador/submit', {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async createAmbassadorCheckout(
    ambassadorId: string
  ): Promise<AmbassadorCheckoutResponse> {
    return fetchApi('/public/ambassador/create-checkout', {
      method: 'POST',
      body: JSON.stringify({ ambassadorId }),
    })
  },

  async getAmbassadorSubmission(
    ambassadorId: string
  ): Promise<{ ambassador: AmbassadorSubmission }> {
    return fetchApi(`/public/ambassador/${ambassadorId}`)
  },

  async validateCoupon(
    code: string,
    applicableTo: string = 'sponsorship_foursome',
    amountCents?: number
  ): Promise<ValidateCouponResponse> {
    return fetchApi('/validate-coupon', {
      method: 'POST',
      body: JSON.stringify({ code, applicableTo, amountCents }),
    })
  },
}

// Admin API
export const adminApi = {
  async getStats(token: string): Promise<Stats> {
    return fetchApiWithAuth('/admin/stats', token)
  },

  async getEvents(token: string): Promise<{ events: Event[] }> {
    return fetchApiWithAuth('/admin/events', token)
  },

  async toggleWaitlistBypass(
    token: string,
    eventId: string,
    enabled: boolean
  ): Promise<{ message: string; event: Event }> {
    return fetchApiWithAuth(`/admin/events/${eventId}/toggle-waitlist-bypass`, token, {
      method: 'POST',
      body: JSON.stringify({ enabled }),
    })
  },

  async toggleWaitlistMode(
    token: string,
    eventId: string,
    enabled: boolean
  ): Promise<{ message: string; event: Event }> {
    return fetchApiWithAuth(`/admin/events/${eventId}/toggle-waitlist-bypass`, token, {
      method: 'POST',
      body: JSON.stringify({ enabled, field: 'waitlistEnabled' }),
    })
  },

  async createManualRegistration(
    token: string,
    data: {
      eventId: string
      primaryContact: { name: string; email: string; phone: string }
      golfersCount: number
      golfers: Array<{ name: string; email: string; phone: string }>
      teamName?: string
      amountPaidCents: number
    }
  ): Promise<{ message: string; registration: Registration }> {
    return fetchApiWithAuth('/admin/registrations/manual', token, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async getRegistrations(
    token: string,
    eventId?: string
  ): Promise<{ registrations: Registration[] }> {
    const query = eventId ? `?eventId=${eventId}` : ''
    return fetchApiWithAuth(`/admin/registrations${query}`, token)
  },

  async getRegistration(
    token: string,
    registrationId: string
  ): Promise<Registration> {
    return fetchApiWithAuth(`/admin/registration/${registrationId}`, token)
  },

  async processRefund(
    token: string,
    registrationId: string
  ): Promise<{ message: string; refundId: string; registration: Registration }> {
    return fetchApiWithAuth(`/admin/registration/${registrationId}/refund`, token, {
      method: 'POST',
    })
  },

  async getTeams(
    token: string,
    eventId?: string
  ): Promise<{ teams: Team[] }> {
    const query = eventId ? `?eventId=${eventId}` : ''
    return fetchApiWithAuth(`/admin/teams${query}`, token)
  },

  async createTeam(
    token: string,
    eventId: string,
    teamName: string,
    notes?: string
  ): Promise<Team> {
    return fetchApiWithAuth('/admin/teams', token, {
      method: 'POST',
      body: JSON.stringify({ eventId, teamName, notes }),
    })
  },

  async addTeamMember(
    token: string,
    teamId: string,
    registrationId: string
  ): Promise<unknown> {
    return fetchApiWithAuth(`/admin/teams/${teamId}/members`, token, {
      method: 'POST',
      body: JSON.stringify({ registrationId }),
    })
  },

  async removeTeamMember(
    token: string,
    teamId: string,
    registrationId: string
  ): Promise<{ message: string }> {
    return fetchApiWithAuth(
      `/admin/teams/${teamId}/members/${registrationId}`,
      token,
      { method: 'DELETE' }
    )
  },

  async deleteTeam(
    token: string,
    teamId: string
  ): Promise<{ message: string }> {
    return fetchApiWithAuth(`/admin/teams/${teamId}`, token, {
      method: 'DELETE',
    })
  },

  async emailTeam(
    token: string,
    teamId: string,
    message: string,
    senderName?: string
  ): Promise<{ message: string; recipientCount: number }> {
    return fetchApiWithAuth(`/admin/teams/${teamId}/email`, token, {
      method: 'POST',
      body: JSON.stringify({ message, senderName }),
    })
  },

  async sendWaitlistInvite(
    token: string,
    registrationId: string
  ): Promise<{ message: string; inviteNumber: number; registration: Registration }> {
    return fetchApiWithAuth(`/admin/waitlist/${registrationId}/send-invite`, token, {
      method: 'POST',
    })
  },

  async sendReminder(
    token: string,
    registrationId: string,
    message?: string,
    sendTo?: 'primary' | 'golfers' | 'all'
  ): Promise<{ message: string; sentEmails: string[]; registration: Registration }> {
    return fetchApiWithAuth(`/admin/registration/${registrationId}/send-reminder`, token, {
      method: 'POST',
      body: JSON.stringify({ message, sendTo }),
    })
  },

  async sendBulkReminder(
    token: string,
    registrationIds: string[],
    message?: string,
    sendTo?: 'primary' | 'golfers' | 'all',
    selectedGolfers?: Record<string, number[]>
  ): Promise<{
    message: string
    sentCount: number
    skippedCount: number
    failedCount: number
    results: Array<{
      registrationId: string
      status: 'sent' | 'skipped' | 'failed'
      email?: string
      emails?: string[]
      reason?: string
    }>
  }> {
    return fetchApiWithAuth('/admin/registrations/send-bulk-reminder', token, {
      method: 'POST',
      body: JSON.stringify({ registrationIds, message, sendTo, selectedGolfers }),
    })
  },

  async sendBulkWaitlistInvite(
    token: string,
    registrationIds: string[]
  ): Promise<{
    message: string
    sentCount: number
    skippedCount: number
    failedCount: number
    results: Array<{
      registrationId: string
      status: 'sent' | 'skipped' | 'failed'
      email?: string
      inviteNumber?: number
      reason?: string
    }>
  }> {
    return fetchApiWithAuth('/admin/waitlist/send-bulk-invite', token, {
      method: 'POST',
      body: JSON.stringify({ registrationIds }),
    })
  },

  // Sponsorship admin endpoints
  async getSponsorshipStats(token: string): Promise<SponsorshipStats> {
    return fetchApiWithAuth('/admin/sponsorships/stats', token)
  },

  async getSponsorships(
    token: string,
    filters?: {
      eventSlug?: string
      status?: string
      type?: string
    }
  ): Promise<{ sponsorships: SponsorshipSubmission[] }> {
    const params = new URLSearchParams()
    if (filters?.eventSlug) params.append('eventSlug', filters.eventSlug)
    if (filters?.status) params.append('status', filters.status)
    if (filters?.type) params.append('type', filters.type)
    const query = params.toString() ? `?${params.toString()}` : ''
    return fetchApiWithAuth(`/admin/sponsorships${query}`, token)
  },

  async getSponsorship(
    token: string,
    submissionId: string
  ): Promise<SponsorshipSubmission> {
    return fetchApiWithAuth(`/admin/sponsorship/${submissionId}`, token)
  },

  async updateSponsorship(
    token: string,
    submissionId: string,
    updates: { adminNotes?: string; pickupStatus?: string; pickupNote?: string; golfFoursomeGolfers?: Array<{ name?: string; email?: string; phone?: string }> }
  ): Promise<SponsorshipSubmission> {
    return fetchApiWithAuth(`/admin/sponsorship/${submissionId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  },

  async exportSponsorships(
    token: string,
    eventSlug?: string
  ): Promise<Blob> {
    const query = eventSlug ? `?eventSlug=${eventSlug}` : ''
    const url = `${config.apiUrl}/admin/sponsorships/export${query}`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (response.status === 401 || response.status === 403) {
      if (authErrorHandler) authErrorHandler()
      throw new AuthError('Your session has expired. Please log in again.', response.status)
    }
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Export failed')
    }
    return response.blob()
  },

  async exportRegistrations(
    token: string,
    eventId?: string
  ): Promise<Blob> {
    const query = eventId ? `?eventId=${eventId}` : ''
    const url = `${config.apiUrl}/admin/registrations/export${query}`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (response.status === 401 || response.status === 403) {
      if (authErrorHandler) authErrorHandler()
      throw new AuthError('Your session has expired. Please log in again.', response.status)
    }
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Export failed')
    }
    return response.blob()
  },

  async exportTeams(
    token: string,
    eventId?: string
  ): Promise<Blob> {
    const query = eventId ? `?eventId=${eventId}` : ''
    const url = `${config.apiUrl}/admin/teams/export${query}`
    const response = await fetch(url, {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    })
    if (response.status === 401 || response.status === 403) {
      if (authErrorHandler) authErrorHandler()
      throw new AuthError('Your session has expired. Please log in again.', response.status)
    }
    if (!response.ok) {
      const data = await response.json()
      throw new Error(data.error || 'Export failed')
    }
    return response.blob()
  },

  async sendUnpaidGolferReminder(
    token: string,
    registrationId: string,
    golferIndex?: number
  ): Promise<{ message: string; sentCount: number; results: Array<{ email: string; golferIndex: number; status: string }> }> {
    return fetchApiWithAuth(`/admin/registration/${registrationId}/unpaid-golfer-reminder`, token, {
      method: 'POST',
      body: JSON.stringify(golferIndex !== undefined ? { golferIndex } : {}),
    })
  },

  async sendSponsorEmail(
    token: string,
    submissionId: string,
    subject: string,
    message: string
  ): Promise<{ message: string }> {
    return fetchApiWithAuth(`/admin/sponsorship/${submissionId}/send-email`, token, {
      method: 'POST',
      body: JSON.stringify({ subject, message }),
    })
  },

  async createManualSponsorship(
    token: string,
    data: {
      eventSlug: string
      sponsor: {
        firstName: string
        lastName: string
        email: string
        phone: string
        businessName?: string
        displayName?: string
      }
      holeSelected?: boolean
      holeAmountCents?: number
      holePrizeDescription?: string
      holePrizeEstimatedAmountCents?: number
      holeTentRequested?: boolean
      tableRunnerAccepted?: boolean
      silentSponsorshipSelected?: boolean
      silentAuctionSelected?: boolean
      silentAuctionPrizeDescription?: string
      silentAuctionPrizeAmountCents?: number
      silentAuctionPrizePickupInfo?: string
      programOption?: 'none' | 'coupon' | 'half' | 'full'
      programOptionSource?: 'none' | 'silentAuctionAddOn' | 'brandedPageSection'
      golfFoursomeSelected?: boolean
      golfFoursomeGolfers?: Array<{ name: string; email: string; phone: string }>
      amountPaidCents?: number
      logoKey?: string
      adminNotes?: string
      sendReceiptEmail?: boolean
      sendGolferEmails?: boolean
    }
  ): Promise<{ message: string; submission: SponsorshipSubmission }> {
    return fetchApiWithAuth('/admin/sponsorships/manual', token, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  // Ambassador admin endpoints
  async getAmbassadors(
    token: string,
    filters?: {
      status?: string
      affiliation?: string
    }
  ): Promise<{ ambassadors: AmbassadorSubmission[]; stats: AmbassadorStats }> {
    const params = new URLSearchParams()
    if (filters?.status) params.append('status', filters.status)
    if (filters?.affiliation) params.append('affiliation', filters.affiliation)
    const query = params.toString() ? `?${params.toString()}` : ''
    return fetchApiWithAuth(`/admin/ambassadors${query}`, token)
  },

  async getAmbassador(
    token: string,
    ambassadorId: string
  ): Promise<{ ambassador: AmbassadorSubmission }> {
    return fetchApiWithAuth(`/admin/ambassador/${ambassadorId}`, token)
  },

  async getLogoDownloadUrl(
    token: string,
    logoKey: string
  ): Promise<{ downloadUrl: string }> {
    return fetchApiWithAuth('/admin/logo-download-url', token, {
      method: 'POST',
      body: JSON.stringify({ logoKey }),
    })
  },

  // Coupon management
  async getCoupons(token: string): Promise<{ coupons: Coupon[] }> {
    return fetchApiWithAuth('/admin/coupons', token)
  },

  async createCoupon(
    token: string,
    data: {
      code: string
      description: string
      discountType: 'fixed' | 'percentage'
      discountValue: number
      applicableTo: 'sponsorship_foursome' | 'registration' | 'both'
      expiresAt?: string | null
    }
  ): Promise<Coupon> {
    return fetchApiWithAuth('/admin/coupons', token, {
      method: 'POST',
      body: JSON.stringify(data),
    })
  },

  async updateCoupon(
    token: string,
    couponId: string,
    updates: Partial<{
      description: string
      discountType: 'fixed' | 'percentage'
      discountValue: number
      applicableTo: 'sponsorship_foursome' | 'registration' | 'both'
      expiresAt: string | null
      active: boolean
    }>
  ): Promise<Coupon> {
    return fetchApiWithAuth(`/admin/coupons/${couponId}`, token, {
      method: 'PATCH',
      body: JSON.stringify(updates),
    })
  },

  async getCouponUsage(
    token: string,
    couponId: string
  ): Promise<{ coupon: Coupon; usage: CouponUsage[]; totalUsage: number }> {
    return fetchApiWithAuth(`/admin/coupons/${couponId}/usage`, token)
  },

  // Silent Auction email
  async getPaidGolfers(
    token: string
  ): Promise<{
    golfers: Array<{
      name: string
      email: string
      source: 'registration' | 'sponsor_foursome'
      teamName?: string
      sponsorName?: string
    }>
    totalCount: number
  }> {
    return fetchApiWithAuth('/admin/paid-golfers', token)
  },

  async sendSilentAuctionEmail(
    token: string,
    recipients: Array<{ email: string; name: string }>
  ): Promise<{
    message: string
    successCount: number
    failureCount: number
    results: Array<{
      email: string
      name: string
      status: 'sent' | 'failed' | 'skipped'
      error?: string
    }>
  }> {
    return fetchApiWithAuth('/admin/email/silent-auction', token, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    })
  },

  async sendSilentAuctionFinalHourEmail(
    token: string,
    recipients: Array<{ email: string; name: string }>
  ): Promise<{
    message: string
    successCount: number
    failureCount: number
    results: Array<{
      email: string
      name: string
      status: 'sent' | 'failed' | 'skipped'
      error?: string
    }>
  }> {
    return fetchApiWithAuth('/admin/email/silent-auction-final-hour', token, {
      method: 'POST',
      body: JSON.stringify({ recipients }),
    })
  },
}
